import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { LearnosityRequest } from '../../models/LearnosityRequest';
import DiagnosticAssessmentWrapper from '../DiagnosticAssessmentWrapper/DiagnosticAssessmentWrapper';
import { DiagnosticSteps } from '../../models/DiagnosticStepsEnum';
import { useLearnosityBffInitialization } from '../../custom-hooks/useLearnosityBffInitialization';
import { diagnosticRegions } from '../../learnosity-regions/DiagnosticRegions';
import { useAppSelector } from '../../redux/hooks.ts';
import { selectUser } from '../../redux/reducers/userSlice.ts';
import { DataLrnSession } from '../../custom-hooks/useLrnSession.ts';
import { useAuth0 } from '@auth0/auth0-react';
import { DiagnosticStateEnum } from './Diagnostic.tsx';
import { usePostHog } from '@posthog/react';
import { isAbortError } from '../../utils/fetchCancellation';
import { ErrorBoundaryUI } from '../ErrorBoundary/ErrorBoundaryUI';

interface Props {
  diagnosticStep: DiagnosticSteps;
  setStep: (step: DiagnosticSteps) => void;
  activityId: string;
  sessionInfo: DataLrnSession | null;
  /** A session lookup is in flight; latching now could capture the snapshot it is about to replace. */
  sessionRefreshing: boolean;
  sessionLookupFailed: boolean;
  setDiagnosticState: Dispatch<SetStateAction<DiagnosticStateEnum | null>>;
}

export const DiagnosticAssessment: FC<Props> = ({
  setStep,
  activityId,
  sessionInfo,
  sessionRefreshing,
  sessionLookupFailed,
  setDiagnosticState,
  diagnosticStep,
}) => {
  const [request, setRequest] = useState<LearnosityRequest | null>(null);
  const [failedStartup, setFailedStartup] = useState<string | null>(null);
  const user = useAppSelector(selectUser);
  const { getAccessTokenSilently, logout } = useAuth0();
  const posthog = usePostHog();
  const { fetchInitialization, refetchInitialization } = useLearnosityBffInitialization(
    getAccessTokenSilently,
    logout,
    posthog,
  );

  // Latch the whole session this assessment started with. A later lookup, such as one after a token
  // refresh before the first save, can return another session for the same learner and activity.
  const learnerId = user?.sub;
  const contextKey = learnerId && diagnosticStep === DiagnosticSteps.ASSESSMENT ? `${learnerId}|${activityId}` : null;
  const [started, setStarted] = useState<{ key: string; session: DataLrnSession } | null>(null);
  if (started && started.key !== contextKey) setStarted(null);
  else if (!started && contextKey && sessionInfo?.sessionId && !sessionRefreshing && !sessionLookupFailed) {
    setStarted({ key: contextKey, session: sessionInfo });
  }
  const startedSession = started?.key === contextKey ? started.session : null;
  const startupKey = contextKey && startedSession ? `${contextKey}|${startedSession.sessionId}` : null;

  useEffect(() => {
    const controller = new AbortController();
    setRequest(null);
    setFailedStartup(null);
    if (learnerId && startedSession) {
      fetchInitialization(
        {
          userId: learnerId,
          sessionId: startedSession.sessionId,
          state: startedSession.status === 'Incomplete' ? 'resume' : 'initial',
          activityId: activityId,
          title: 'Diagnostic',
          regions: diagnosticRegions,
          renderingType: 'assess',
          responsiveRegions: true,
          scrollToTest: false,
          scrollToTop: false,
          showIntro: false,
          warningOnChange: false,
        },
        { signal: controller.signal },
      )
        .then(data => {
          if (controller.signal.aborted) return;
          setRequest(data);
        })
        .catch(error => {
          if (controller.signal.aborted || isAbortError(error)) return;
          setFailedStartup(startupKey);
          console.log('Error fetching Learnosity bridge');
        });
    }
    return () => controller.abort();
  }, [learnerId, activityId, startedSession, startupKey]);

  return (
    <>
      {((!startedSession && sessionLookupFailed) || (failedStartup !== null && failedStartup === startupKey)) && (
        <ErrorBoundaryUI embedded />
      )}
      {request && startedSession && (
        <div className="w-full" id="diagnosticUIStep2">
          <DiagnosticAssessmentWrapper
            sessionInfo={startedSession}
            setStep={setStep}
            request={request!}
            refetchInitialization={refetchInitialization}
            setDiagnosticState={setDiagnosticState}
          />
        </div>
      )}
    </>
  );
};
